import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { blogPosts } from "@/data";
import { formatDate } from "@/lib/utils";
import { JSX } from "react/jsx-runtime";

export function BlogPreviewSection(): JSX.Element {
  const featured = blogPosts.filter((p) => p.featured).slice(0, 3);

  return (
    <section className="py-24 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl font-semibold text-[#1A1A1A]">
              Latest Updates
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B1E2A] hover:text-[#4A1219] transition-colors"
            >
              View All Articles
              <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featured.map((post, i) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group"
            >
              <Link to={`/blog/${post.slug}` as never}>
                <div className="aspect-[16/9] bg-[#6B1E2A]/8 rounded-xl mb-5 overflow-hidden relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#6B1E2A]/15 flex items-center justify-center">
                      <span className="font-serif text-2xl font-bold text-[#6B1E2A]/40">
                        {post.category.charAt(0)}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-4 text-xs text-[#6B6B6B] mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={11} />
                      {formatDate(post.publishedAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {post.readTime} min read
                    </span>
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A] leading-snug mb-3 group-hover:text-[#6B1E2A] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-[#6B6B6B] leading-relaxed line-clamp-2 mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-[#6B1E2A]/10 rounded-full flex items-center justify-center">
                      <span className="text-[#6B1E2A] text-xs font-bold">
                        {post.author.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-[#1A1A1A]">
                      {post.author.name}
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
